import React, { useCallback, useMemo, useState } from "react";
import { Counterparty } from "@common/types/counterparty";
import { pluralizeWithCount } from "@common/helpers/string";
import { CounterpartyCombobox } from "@components/transactions/TransactionSplitV2/CounterpartyCombobox";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import {
    isCounterpartyLockedTransactionsError,
    useCounterpartyMergingMutation,
} from "@api/counterparty.api";
import { Button } from "@design-system/Button/Button";
import { ModalComponentProps } from "@helpers/modal";
import { HStack } from "@design-system/HStack/HStack";
import {
    StandardModalBody,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { DisableCounterpartyInsteadModal } from "./DisableCounterpartyInsteadModal";

interface Props extends ModalComponentProps {
    counterparty: Counterparty;
    journalEntryLinesCount?: number;
}

export const MergeCounterpartyModal: React.FC<Props> = ({
    counterparty,
    journalEntryLinesCount,
    close,
}) => {
    const [counterpartyToKeep, setCounterpartyToKeep] =
        useState<Pick<Counterparty, "id" | "name">>();
    const [showDisableInsteadModal, setShowDisableInsteadModal] =
        useState(false);
    const excludeIds = useMemo(() => [counterparty.id], [counterparty.id]);
    const merging = useCounterpartyMergingMutation();

    const performMerge = useCallback(
        async ({
            shouldDisableInstead,
        }: {
            shouldDisableInstead?: boolean;
        }) => {
            if (!counterpartyToKeep) {
                return;
            }
            await merging.mutateAsync({
                counterpartyToMergeId: counterparty.id,
                counterpartyToKeepId: counterpartyToKeep.id,
                disableCounterpartyToMerge: shouldDisableInstead,
            });
            close();
        },
        [merging, counterpartyToKeep, counterparty.id, close],
    );

    const onSubmit = useCallback(async () => {
        try {
            await performMerge({});
        } catch (e) {
            if (
                isCounterpartyLockedTransactionsError(e) &&
                counterparty.workspaceId
            ) {
                setShowDisableInsteadModal(true);
            } else {
                throw e;
            }
        }
    }, [performMerge, counterparty.workspaceId]);

    const onDisableInstead = useCallback(async () => {
        await performMerge({ shouldDisableInstead: true });
        setShowDisableInsteadModal(false);
    }, [performMerge]);

    return (
        <>
            <StandardModalHeader>Merge {counterparty.name}</StandardModalHeader>
            <StandardModalBody>
                <p>
                    Select the counterparty to merge{" "}
                    <strong className="font-weight-medium">
                        {counterparty.name}
                    </strong>{" "}
                    into.
                </p>

                <form>
                    <CounterpartyCombobox
                        selectedCounterparty={counterpartyToKeep}
                        onChange={setCounterpartyToKeep}
                        hideFooter
                        excludeIds={excludeIds}
                        triggerButtonProps={{
                            variant: "primary",
                            block: true,
                            size: "xl",
                        }}
                    />

                    {counterpartyToKeep && (
                        <p className="mt-3 mb-0">
                            {journalEntryLinesCount ? (
                                <>
                                    <strong className="font-weight-medium">
                                        {counterparty.name}
                                    </strong>{" "}
                                    will be removed and{" "}
                                    {pluralizeWithCount(
                                        "journal entry line",
                                        journalEntryLinesCount,
                                    )}{" "}
                                    will be reassigned to{" "}
                                    <strong className="font-weight-medium">
                                        {counterpartyToKeep.name}
                                    </strong>
                                    .
                                </>
                            ) : (
                                <>
                                    <strong className="font-weight-medium">
                                        {counterparty.name}
                                    </strong>{" "}
                                    will be merged into{" "}
                                    <strong className="font-weight-medium">
                                        {counterpartyToKeep.name}
                                    </strong>
                                    .
                                </>
                            )}
                        </p>
                    )}

                    <HStack as="footer" justify="end" gap={12} className="mt-5">
                        <Button
                            variant="secondary"
                            size="md"
                            type="button"
                            onClick={close}
                        >
                            Cancel
                        </Button>
                        <ButtonWithLoader
                            variant="primary"
                            size="md"
                            type="button"
                            loading={merging.isPending}
                            disabled={!counterpartyToKeep}
                            onClick={onSubmit}
                        >
                            Merge
                        </ButtonWithLoader>
                    </HStack>
                </form>
            </StandardModalBody>
            <DisableCounterpartyInsteadModal
                show={showDisableInsteadModal}
                counterparty={counterparty}
                mergeTargetName={counterpartyToKeep?.name}
                onConfirm={onDisableInstead}
                onHide={() => setShowDisableInsteadModal(false)}
            />
        </>
    );
};
